/**
 * Enterprise Slideshow — component-slideshow.js
 * Engine: Splide 4.x (loaded separately as splide.min.js)
 *
 * Usage: add [data-slideshow] to the .splide wrapper element.
 * All config is read from data-* attributes; no inline JS needed.
 *
 * Covers:
 *   - Splide initialization with config from data attrs
 *   - Video: autoplay muted, pause on leave, silent failure
 *   - Content animations: re-triggered on slide change
 *   - Shopify Theme Editor lifecycle (all 6 events)
 *   - Anti-duplication guard
 *   - Two-instance safe (one EnterpriseSlideshow per element)
 *   - 1-slide and 0-slide edge cases
 *   - Clone-aware block→slide index mapping for loop mode
 */

(function () {
  'use strict';

  /* ----------------------------------------------------------
     Instance registry — key: sectionId, value: EnterpriseSlideshow
  ---------------------------------------------------------- */
  const REGISTRY = new Map();

  /* ----------------------------------------------------------
     Helpers
  ---------------------------------------------------------- */
  function boolAttr(el, attr, fallback) {
    if (!el.hasAttribute(attr)) return fallback;
    const v = el.getAttribute(attr);
    return v === 'true' || v === '1';
  }

  function intAttr(el, attr, fallback) {
    const v = parseInt(el.getAttribute(attr), 10);
    return isNaN(v) ? fallback : v;
  }

  /* ----------------------------------------------------------
     EnterpriseSlideshow class
  ---------------------------------------------------------- */
  class EnterpriseSlideshow {
    /**
     * @param {HTMLElement} el  — the .es-slideshow.splide element
     */
    constructor(el) {
      if (!el) return;
      if (el.hasAttribute('data-slideshow-initialized')) return;
      el.setAttribute('data-slideshow-initialized', '1');

      this.el = el;
      this.splide = null;
      this.sectionId = el.getAttribute('data-section-id') || null;

      this._init();
    }

    /* --------------------------------------------------------
       Splide init
    -------------------------------------------------------- */
    _buildConfig() {
      const el = this.el;
      const transition = el.getAttribute('data-transition') || 'fade';
      const autoplay = boolAttr(el, 'data-autoplay', true);
      const speed = intAttr(el, 'data-autoplay-speed', 7) * 1000;
      const loop = boolAttr(el, 'data-loop', true);

      // Count real (non-clone) slides
      const slides = el.querySelectorAll('.splide__slide');
      const slideCount = slides.length;

      const forceSingle = slideCount <= 1;

      return {
        type: transition === 'fade' ? 'fade' : 'slide',
        rewind: loop && !forceSingle,
        speed: 600,
        rewindSpeed: 600,
        autoplay: autoplay && !forceSingle,
        interval: speed,
        pauseOnHover: boolAttr(el, 'data-pause-on-hover', true),
        arrows: boolAttr(el, 'data-show-arrows', true) && !forceSingle,
        pagination: boolAttr(el, 'data-show-dots', true) && !forceSingle,
        // Reduced-motion: Splide respects prefers-reduced-motion natively
        // We also add inline override below
        reducedMotion: {
          speed: 0,
          rewindSpeed: 0,
          autoplay: false,
        },
        // Accessibility
        i18n: {
          prev: 'Previous slide',
          next: 'Next slide',
          first: 'Go to first slide',
          last: 'Go to last slide',
          slideX: 'Go to slide %s',
          pageX: 'Go to page %s',
          play: 'Start autoplay',
          pause: 'Stop autoplay',
        },
      };
    }

    _init() {
      const el = this.el;

      // Guard: 0 slides
      const slideEls = el.querySelectorAll('.splide__slide');
      if (slideEls.length === 0) {
        el.classList.add('es-slideshow--empty');
        return;
      }

      if (typeof Splide === 'undefined') {
        console.warn('[EnterpriseSlideshow] Splide not loaded. Deferring…');
        window.addEventListener('load', () => this._init(), {once: true});
        return;
      }

      const config = this._buildConfig();
      this.splide = new Splide(el, config);

      // Bind events before mount so we don't miss the first active
      this.splide.on('mounted', () => {
        this._onSlideChange(this.splide.index);
      });

      this.splide.on('moved', (newIndex) => {
        this._onSlideChange(newIndex);
      });

      this.splide.mount();
    }

    /* --------------------------------------------------------
       Per-slide logic: videos + content animations
    -------------------------------------------------------- */
    _onSlideChange(activeIndex) {
      const slides = this.el.querySelectorAll(
        '.splide__slide:not(.splide__slide--clone)',
      );

      slides.forEach((slide, i) => {
        const isActive = i === activeIndex;
        const video = slide.querySelector('[data-es-video]');

        if (video) {
          if (isActive) {
            this._playVideo(video);
          } else {
            this._pauseVideo(video);
          }
        }
      });
    }

    _playVideo(video) {
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          // Autoplay blocked or element removed — silent failure
        });
      }
    }

    _pauseVideo(video) {
      try {
        if (!video.paused) {
          video.pause();
        }
      } catch (_) {
        // Defensive: element might have been removed from DOM
      }
    }

    /* --------------------------------------------------------
       Block → real slide index (clone-safe)
    -------------------------------------------------------- */
    _indexForBlock(blockId) {
      const realSlides = this.el.querySelectorAll(
        '.splide__slide:not(.splide__slide--clone)',
      );
      for (let i = 0; i < realSlides.length; i++) {
        if (realSlides[i].getAttribute('data-block-id') === blockId) {
          return i;
        }
      }
      return -1;
    }

    /* --------------------------------------------------------
       Public API
    -------------------------------------------------------- */
    goToBlock(blockId) {
      if (!this.splide) return;
      const idx = this._indexForBlock(blockId);
      if (idx >= 0) {
        this.splide.go(idx);
      }
    }

    pauseAutoplay() {
      this.splide?.Components?.Autoplay?.pause();
    }

    resumeAutoplay() {
      this.splide?.Components?.Autoplay?.play();
    }

    destroy() {
      // Pause all videos before destroy
      this.el
        .querySelectorAll('[data-es-video]')
        .forEach((v) => this._pauseVideo(v));

      if (this.splide) {
        try {
          this.splide.destroy();
        } catch (_) {
          // May already be destroyed
        }
        this.splide = null;
      }

      this.el.removeAttribute('data-slideshow-initialized');
    }
  }

  /* ----------------------------------------------------------
     Module init — scan for [data-slideshow] and boot instances
  ---------------------------------------------------------- */
  function initAll(root) {
    const target = root || document;
    target
      .querySelectorAll('[data-slideshow]:not([data-slideshow-initialized])')
      .forEach((el) => {
        const sectionId = el.getAttribute('data-section-id');
        const instance = new EnterpriseSlideshow(el);
        if (sectionId) {
          REGISTRY.set(sectionId, instance);
        }
      });
  }

  /* ----------------------------------------------------------
     Shopify Theme Editor lifecycle
  ---------------------------------------------------------- */
  function getInstanceFromEvent(e) {
    const sectionId = e.detail?.sectionId;
    if (!sectionId) return null;
    return REGISTRY.get(sectionId) || null;
  }

  // section:load — new DOM injected, find and init the slideshow
  document.addEventListener('shopify:section:load', (e) => {
    const sectionEl = e.target;
    if (!sectionEl) return;

    // Destroy any stale instance with the same sectionId first
    const sectionId = e.detail?.sectionId;
    if (sectionId && REGISTRY.has(sectionId)) {
      REGISTRY.get(sectionId).destroy();
      REGISTRY.delete(sectionId);
    }

    const slideshowEls = sectionEl.querySelectorAll('[data-slideshow]');
    slideshowEls.forEach((el) => {
      const id = el.getAttribute('data-section-id') || sectionId;
      const instance = new EnterpriseSlideshow(el);
      if (id) REGISTRY.set(id, instance);
    });
  });

  // section:unload — tear down
  document.addEventListener('shopify:section:unload', (e) => {
    const instance = getInstanceFromEvent(e);
    if (instance) {
      instance.destroy();
      REGISTRY.delete(e.detail.sectionId);
    }
  });

  // section:select — editor focused on this section; pause autoplay
  document.addEventListener('shopify:section:select', (e) => {
    getInstanceFromEvent(e)?.pauseAutoplay();
  });

  // section:deselect — editor unfocused; resume
  document.addEventListener('shopify:section:deselect', (e) => {
    getInstanceFromEvent(e)?.resumeAutoplay();
  });

  // block:select — navigate to the slide being edited
  document.addEventListener('shopify:block:select', (e) => {
    const instance = getInstanceFromEvent(e);
    if (!instance) return;
    instance.pauseAutoplay();
    const blockId = e.detail?.blockId;
    if (blockId) {
      instance.goToBlock(blockId);
    }
  });

  // block:deselect — resume autoplay
  document.addEventListener('shopify:block:deselect', (e) => {
    getInstanceFromEvent(e)?.resumeAutoplay();
  });

  /* ----------------------------------------------------------
     Boot on DOMContentLoaded (or immediately if already loaded)
  ---------------------------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Expose for debugging in console
  window.EnterpriseSlideshow = EnterpriseSlideshow;
  window._slideshowRegistry = REGISTRY;
})();
